import type { WebsiteModule } from '@/interfaces/module.interface';
import type { WebsiteComponent } from '@/interfaces/component.interface';
import type { UserConfig, GlobalConfig, SiteSettings } from '@/types/builder';
import type { ResponsiveValue } from '@/types/builder';
import { CUSTOM_CODE_ELEMENTS } from '@/types/builder';
import { extractOid, normalizeOid } from '@/utils/normalizeOid';

/**
 * Converts camelCase to snake_case
 */
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Converts snake_case to camelCase
 */
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Converts an object's keys from snake_case to camelCase
 * Handles API responses that may be in snake_case format
 */
const convertKeysToCamelCase = <T extends Record<string, unknown>>(
  obj: T
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    // If key contains underscore, convert to camelCase
    const camelKey = key.includes('_') ? toCamelCase(key) : key;
    result[camelKey] = value;
  }
  return result;
};

/** JSON columns on website_modules must be sent as JSON strings to the upstream API. */
const MODULE_JSON_FIELD_KEYS = new Set([
  'typography',
  'colors',
  'breakpoints',
  'favicons',
  'analytics_tracking',
  'custom_code',
  'performance_accessibility',
  'seo',
  'security_privacy',
  'project_metadata',
  'environment_urls',
  'devices_q_a_matrix',
  'qa_success',
  'tech_stack',
  'copy_legal',
  'brand_colors',
  'base_domain',
  'custom_domain',
  'flags',
]);

const stringifyJsonField = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
};

/**
 * Normalizes a module write payload for the upstream entities API:
 * - drops null/undefined values
 * - omits `id` (update id lives in the URL)
 * - stringifies JSON column fields
 */
export const prepareModulePayloadForApi = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null || key === 'id') {
      continue;
    }

    if (MODULE_JSON_FIELD_KEYS.has(key)) {
      const stringified = stringifyJsonField(value);
      if (stringified !== undefined) {
        result[key] = stringified;
      }
      continue;
    }

    result[key] = value;
  }

  return result;
};

const KNOWN_INDUSTRY_TYPES = new Set([
  'blankTemplate',
  'template1',
  'template2',
  'template3',
  'template4',
]);

/** Resolve industry_type (component id or template slug) to the builder template slug. */
const resolveIndustryTemplateType = (
  industryRef: string,
  components?: WebsiteComponent[],
): string => {
  if (KNOWN_INDUSTRY_TYPES.has(industryRef)) {
    return industryRef;
  }

  const matchingComponent = components?.find(
    (component) => component.id === industryRef || component.type === industryRef,
  );
  if (matchingComponent?.type) {
    return matchingComponent.type;
  }

  return industryRef;
};

/** Resolve builder industry (template slug or component id) to a component id for industry_type. */
const resolveIndustryComponentId = (
  industryRef: string,
  components?: WebsiteComponent[],
): string => {
  const matchingComponent = components?.find(
    (component) => component.type === industryRef || component.id === industryRef,
  );
  if (matchingComponent?.id) {
    return matchingComponent.id;
  }

  return industryRef;
};

/**
 * Maps UserConfig and SiteSettings to WebsiteModule interface.
 * Note: Returns data with snake_case field names (project_id, industry_type, etc.) for API requests.
 * industry_type will be set to the component ID that matches the industry type.
 */
export const mapBuilderConfigToModule = (
  projectId: string,
  userConfig: UserConfig | null,
  generalSettings?: SiteSettings,
  components?: WebsiteComponent[]
): Partial<WebsiteModule> & { website_project_id: string } => {
  const resolvedProjectId = extractOid(projectId) ?? projectId;

  if (!userConfig) {
    // Return with snake_case field names for API requests
    return {
      website_project_id: resolvedProjectId,
      name: '',
      industry_type: 'blankTemplate',
    } as Partial<WebsiteModule> & { website_project_id: string };
  }

  // industry_type stores the website_components id; userConfig.industry is the template slug
  // but may already be a component id when loaded back from the API.
  let industryTypeValue = resolveIndustryComponentId(
    userConfig.industry || 'blankTemplate',
    components,
  );
  if (!components?.length) {
    console.warn(
      '⚠️ No components provided to mapBuilderConfigToModule. Using industry value as-is:',
      industryTypeValue,
    );
  }

  // Preserve responsive logo variants when available.
  // Note: backend `brand_logo` may be a string column, so store responsive values as JSON string.
  let brandLogo: string | ResponsiveValue<string> | undefined;
  if (userConfig.logo) {
    if (typeof userConfig.logo === 'string') {
      brandLogo = userConfig.logo;
    } else if (userConfig.logo.desktop) {
      brandLogo = JSON.stringify({
        desktop: userConfig.logo.desktop,
        tablet: userConfig.logo.tablet,
        mobile: userConfig.logo.mobile,
      });
    }
  }

  // Map favicons
  const favicons: Record<string, unknown> = {};
  if (generalSettings) {
    if (generalSettings.favicon32) {
      favicons.favicon32 =
        typeof generalSettings.favicon32 === 'string'
          ? generalSettings.favicon32
          : undefined;
    }
    if (generalSettings.favicon48) {
      favicons.favicon48 =
        typeof generalSettings.favicon48 === 'string'
          ? generalSettings.favicon48
          : undefined;
    }
    if (generalSettings.favicon180) {
      favicons.favicon180 =
        typeof generalSettings.favicon180 === 'string'
          ? generalSettings.favicon180
          : undefined;
    }
  }

  // Build module object with camelCase keys first (for TypeScript interface compatibility)
  const moduleCamelCase = {
    website_project_id: resolvedProjectId, // Already snake_case
    name: userConfig.businessName || '',
    industryType: industryTypeValue, // Use component ID instead of type
    typography: userConfig.globalConfig?.typography
      ? JSON.stringify(userConfig.globalConfig.typography)
      : undefined,
    colors: userConfig.globalConfig?.colors
      ? JSON.stringify(userConfig.globalConfig.colors)
      : undefined,
    breakpoints: userConfig.globalConfig?.breakpoints
      ? JSON.stringify(userConfig.globalConfig.breakpoints)
      : undefined,
    brandLogo,
    favicons:
      Object.keys(favicons).length > 0 ? JSON.stringify(favicons) : undefined,
    socialPreview:
      generalSettings?.socialPreview &&
      typeof generalSettings.socialPreview === 'string'
        ? generalSettings.socialPreview
        : undefined,
    appleTouchIcon:
      generalSettings?.appleTouchIcon &&
      typeof generalSettings.appleTouchIcon === 'string'
        ? generalSettings.appleTouchIcon
        : undefined,
    language: generalSettings?.language,
    description: (() => {
      // Format current time as YYYY-MM-DD HH:MM:SS
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    })(),
    googleAnalyticsMeasurementId:
      generalSettings?.googleAnalyticsMeasurementId || undefined,
    analyticsTracking: generalSettings?.googleAnalyticsMeasurementId
      ? JSON.stringify({
          googleAnalytics: {
            measurementId: generalSettings.googleAnalyticsMeasurementId,
          },
        })
      : undefined,
    customCode: generalSettings?.customCode
      ? JSON.stringify(generalSettings.customCode)
      : undefined,
    performanceAccessibility: generalSettings?.performanceAccessibility
      ? JSON.stringify(generalSettings.performanceAccessibility)
      : undefined,
    seo: generalSettings?.seo
      ? JSON.stringify(generalSettings.seo)
      : undefined,
    securityPrivacy: undefined, // Can be extended later
    projectMetadata: generalSettings?.projectMetadata
      ? JSON.stringify(generalSettings.projectMetadata)
      : undefined,
    environmentUrls: generalSettings?.projectMetadata?.environmentUrls
      ? JSON.stringify(generalSettings.projectMetadata.environmentUrls)
      : undefined,
    devicesQAMatrix: undefined, // Can be extended later
    qaSuccess: undefined, // Can be extended later
    techStack: undefined, // Can be extended later
    copyLegal: generalSettings?.copyLegal
      ? JSON.stringify(generalSettings.copyLegal)
      : undefined,
    brandColors: generalSettings?.brandColors
      ? JSON.stringify(generalSettings.brandColors)
      : undefined,
    baseDomain: generalSettings?.baseDomain
      ? JSON.stringify(generalSettings.baseDomain)
      : undefined,
    customDomain: generalSettings?.customDomain
      ? JSON.stringify(generalSettings.customDomain)
      : undefined,
    fallbackUrl: generalSettings?.baseDomain?.url,
    flags: undefined, // Can be extended later
  };

  // Remove undefined values first
  const cleanedModule = Object.fromEntries(
    Object.entries(moduleCamelCase).filter(([_, value]) => value !== undefined)
  );

  // Convert camelCase keys to snake_case for API request
  // Special handling for keys that are already snake_case (project_id, created_at, updated_at)
  const moduleSnakeCase: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cleanedModule)) {
    // Keep snake_case keys as-is, convert camelCase to snake_case
    if (key.includes('_')) {
      moduleSnakeCase[key] = value;
    } else {
      moduleSnakeCase[toSnakeCase(key)] = value;
    }
  }

  // Return with snake_case field names for API requests
  return prepareModulePayloadForApi(
    normalizeOid(moduleSnakeCase) as Record<string, unknown>,
  ) as Partial<WebsiteModule> & { website_project_id: string };
};

/**
 * Maps WebsiteModule to UserConfig and SiteSettings
 * Handles both camelCase and snake_case field names from API
 */
export const mapModuleToBuilderConfig = (
  module: WebsiteModule | Record<string, unknown>,
  components?: WebsiteComponent[],
): {
  userConfig: UserConfig;
  generalSettings?: SiteSettings;
} => {
  // Convert snake_case keys to camelCase if needed (API might return snake_case)
  const normalizedModule = convertKeysToCamelCase(
    normalizeOid(module) as Record<string, unknown>,
  ) as unknown as WebsiteModule;

  // Helper to safely parse JSON strings or return the value as-is if already parsed
  const safeJsonParse = <T>(value: unknown, fallback: T): T => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed as T;
      } catch {
        return fallback;
      }
    }
    return value as T;
  };

  const parsedTypography = safeJsonParse<GlobalConfig['typography']>(
    normalizedModule.typography,
    []
  );

  const parsedColors = safeJsonParse<GlobalConfig['colors']>(
    normalizedModule.colors,
    {
      background: '#FFFFFF',
      forground: '#000000',
      primary: '#7C3AED',
      secondary: '#6366F1',
      tertiary: '#F59E42',
      quaternary: '#10B981',
      link: '#2563EB',
      activeLink: '#1D4ED8',
    }
  );

  const parsedBreakpoints = safeJsonParse<GlobalConfig['breakpoints']>(
    normalizedModule.breakpoints,
    {
      mobileMax: 640,
      tabletMin: 641,
      tabletMax: 1024,
      desktopMin: 1025,
    }
  );

  const parseResponsiveBrandLogo = (
    brandLogo: unknown
  ): string | ResponsiveValue<string> | undefined => {
    if (!brandLogo) return undefined;

    if (typeof brandLogo === 'object') {
      return brandLogo as ResponsiveValue<string>;
    }

    if (typeof brandLogo !== 'string') return undefined;

    const trimmed = brandLogo.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as ResponsiveValue<string>;
        if (parsed && typeof parsed === 'object' && 'desktop' in parsed) {
          return parsed;
        }
      } catch {
        // Fall back to plain string logo below.
      }
    }

    return brandLogo;
  };

  const normalizedBrandLogo = parseResponsiveBrandLogo(normalizedModule.brandLogo);

  const industryTypeRaw =
    extractOid(normalizedModule.industryType) ??
    extractOid((normalizedModule as WebsiteModule & { industry_type?: unknown }).industry_type) ??
    String(normalizedModule.industryType ?? 'blankTemplate');

  const userConfig: UserConfig = {
    businessName: normalizedModule.name || '',
    industry: resolveIndustryTemplateType(
      industryTypeRaw,
      components,
    ) as UserConfig['industry'],
    logo: normalizedBrandLogo,
    globalConfig: {
      typography: Array.isArray(parsedTypography) ? parsedTypography : [],
      colors: parsedColors,
      breakpoints: parsedBreakpoints,
    },
  };

  const generalSettings: SiteSettings = {
    title: normalizedModule.name || '',
    language: normalizedModule.language || 'en',
    description: normalizedModule.description || '',
    favicon32:
      normalizedModule.favicons &&
      typeof normalizedModule.favicons.favicon32 === 'string'
        ? (normalizedModule.favicons.favicon32 as string)
        : undefined,
    favicon48:
      normalizedModule.favicons &&
      typeof normalizedModule.favicons.favicon48 === 'string'
        ? (normalizedModule.favicons.favicon48 as string)
        : undefined,
    favicon180:
      normalizedModule.favicons &&
      typeof normalizedModule.favicons.favicon180 === 'string'
        ? (normalizedModule.favicons.favicon180 as string)
        : undefined,
    socialPreview: normalizedModule.socialPreview
      ? (normalizedModule.socialPreview as unknown as File)
      : undefined,
    appleTouchIcon: normalizedModule.appleTouchIcon
      ? (normalizedModule.appleTouchIcon as unknown as File)
      : undefined,
    googleAnalyticsMeasurementId: normalizedModule.googleAnalyticsMeasurementId,
    customCode: normalizedModule.customCode
      ? (normalizedModule.customCode as SiteSettings['customCode'])
      : {
          [CUSTOM_CODE_ELEMENTS.START_OF_HEAD_TAG]: '',
          [CUSTOM_CODE_ELEMENTS.END_OF_HEAD_TAG]: '',
          [CUSTOM_CODE_ELEMENTS.START_OF_BODY_TAG]: '',
          [CUSTOM_CODE_ELEMENTS.END_OF_BODY_TAG]: '',
        },
    page: 'home',
    showPageOnSearchEngine: true,
    baseDomain: normalizedModule.baseDomain
      ? (normalizedModule.baseDomain as any)
      : undefined,
    customDomain: normalizedModule.customDomain
      ? (normalizedModule.customDomain as any)
      : undefined,
    brandLogo: normalizedBrandLogo,
    brandColors: normalizedModule.brandColors
      ? (normalizedModule.brandColors as any)
      : undefined,
    projectMetadata: normalizedModule.projectMetadata
      ? (normalizedModule.projectMetadata as any)
      : {
          projectName: '',
          projectCode: '',
          stakeholders: '',
          owners: '',
          timeline: {
            designPhase: '',
            developmentPhase: '',
            qaPhase: '',
            goLivePhase: '',
          },
          deploymentTarget: '',
          cmsType: 'html' as const,
          environmentUrls: {
            staging: '',
            development: '',
            production: '',
          },
        },
    copyLegal: normalizedModule.copyLegal
      ? (normalizedModule.copyLegal as any)
      : {
          finalCopyDocs: '',
          ctaVerbiage: '',
          legalApproval: false,
          emailContact: '',
          phoneContact: '',
        },
    performanceAccessibility: normalizedModule.performanceAccessibility
      ? (normalizedModule.performanceAccessibility as any)
      : {
          imageOptimizationRules: {
            maxFileSize: 500,
            maxDimensions: {
              width: 1920,
              height: 1080,
            },
            formats: ['webp', 'jpg', 'png'],
          },
          lazyLoadSettings: {
            enabled: true,
            threshold: 0.1,
          },
          accessibilityTags: {
            altTextRequired: true,
            tabIndexEnabled: true,
            colorContrastCheck: true,
          },
          fontLoadingStrategy: 'swap' as const,
          pageSpeedTargets: {
            mobileScore: 80,
            desktopScore: 90,
          },
        },
    seo: safeJsonParse(normalizedModule.seo, {
      metaTitle: normalizedModule.name || '',
      metaDescription: normalizedModule.description || '',
      canonicalTags: true,
      openGraphTags: {
        title: normalizedModule.name || '',
        description: normalizedModule.description || '',
        image: normalizedModule.socialPreview || '',
        type: 'website',
      },
      altTextForSEO: true,
    }),
    // Add missing optional properties
    analyticsTracking: normalizedModule.analyticsTracking as any,
    techStack: normalizedModule.techStack as any,
    qaSuccess: normalizedModule.qaSuccess as any,
    securityPrivacy: normalizedModule.securityPrivacy as any,
  };

  return { userConfig, generalSettings };
};
